import * as React from 'react';

import { useGetState, useLockFn } from 'ahooks';
import { Flex, Form, notification } from 'antd';

import { PrimitiveForm } from 'components';
import { bulkUpdateProducts } from 'services';
import type {
  ProductAmountType,
  ProductPriceType,
  TreeSelectSource,
} from 'types';
import { fixedParseFloat, normalizeToString } from 'utils';

import { ProductsEditFormAction } from './action';
import { ProductsEditFormContent } from './content';

type FieldType = Partial<
  Pick<
    Parameters<typeof bulkUpdateProducts>[number],
    'brandsIds' | 'statuses'
  > &
    Record<'categoriesIds', TreeSelectSource[]>
>;

const ProductsEditForm = () => {
  const [form] = Form.useForm<FieldType>();

  const [priceType, setPriceType] = useGetState<ProductPriceType>();
  const [priceAmount, setPriceAmount] = useGetState<number>();
  const [amountType, setAmountType] =
    useGetState<ProductAmountType>('Increase');
  const [[lowerBoundCreateDate, upperBoundCreateDate], setDurationRange] =
    useGetState<[string?, string?]>([]);

  const formattedPriceAmount = React.useMemo<number | undefined>(() => {
    const isPercentageType = priceType === 'Percentage';
    const isNumberAmount = typeof priceAmount === 'number';

    if (!isNumberAmount) return;

    const fixedPriceAmount = fixedParseFloat(priceAmount, 3);
    const priceAmountValue = Math.min(Math.max(-100, fixedPriceAmount), 100);

    if (!isPercentageType) return fixedPriceAmount;

    return priceAmountValue;
  }, [priceType, priceAmount]);

  const onFinish = useLockFn(
    async ({
      statuses = [],
      brandsIds = [],
      categoriesIds = [],
    }: FieldType) => {
      const hasBrands = brandsIds.length > 0;
      const hasCategories = categoriesIds.length > 0;
      const hasStatuses = statuses.length > 0;
      const hasDuration = lowerBoundCreateDate || upperBoundCreateDate;

      const allowedToUpdate =
        hasBrands || hasCategories || hasStatuses || hasDuration;

      if (!allowedToUpdate) {
        notification.info({
          message: 'اطلاعات مورد نیاز را تکمیل کنید.',
        });
        return;
      }

      const amountSign = amountType === 'Increase' ? 1 : -1;
      const amountValue = (formattedPriceAmount || 0) * amountSign;

      const params: Parameters<typeof bulkUpdateProducts>[0] = {
        statuses,
        brandsIds,
        lowerBoundCreateDate,
        upperBoundCreateDate,
        type: priceType!,
        amount: amountValue,
        categoriesIds: categoriesIds.map(({ value }) =>
          normalizeToString(value),
        ),
      };

      bulkUpdateProducts(params)
        .then(() => {
          notification.success({
            message: 'تغییرات مورد نظر با موفقیت اعمال شد.',
          });
        })
        .catch(message => {
          notification.error({ message });
        });
    },
  );

  return (
    <PrimitiveForm
      form={form}
      fields={[
        {
          name: 'type',
          value: priceType,
        },
        {
          name: 'amount',
          value: formattedPriceAmount,
        },
        {
          name: 'amountType',
          value: amountType,
        },
      ]}
      onFinish={onFinish}
    >
      <Flex
        className='*:flex-1 *:rounded *:bg-white max-xl:flex-col max-xl:last:*:sticky max-xl:last:*:inset-x-0 max-xl:last:*:bottom-0 max-xl:last:*:z-10 max-xl:last:*:shadow-inner xl:items-start xl:first:*:max-w-[70%] xl:last:*:max-w-[30%]'
        gap='large'
      >
        <ProductsEditFormContent
          priceType={priceType}
          setPriceType={setPriceType}
          setPriceAmount={setPriceAmount}
          setAmountType={setAmountType}
          setDurationRange={setDurationRange}
        />
        <ProductsEditFormAction />
      </Flex>
    </PrimitiveForm>
  );
};

export { ProductsEditForm };
